'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the tickets fix these in pixels (75px logo tile, the × offset from the tile's outer edge, the tray's drop shadow) and no token carries them; the desktop rows reuse the page's content-width variable; z-[60] keeps the docked tray above the page's sticky layers (50) */

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import Button from '../../components/Button';
import { CmsImage } from '../../components/CmsImage';
import { MaterialIcon } from '../../components/MaterialIcon';
import { usePinnedBottomHeight } from '../../hooks/usePinnedBottomHeight';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { useCompare } from './CompareProvider';
import {
  COMPARE_VARIANT_LAYOUT,
  compareImageOf,
  compareVariantCopy,
  compareVariantOf,
} from './compareVariant';
import { TRANSLATIONS } from './i18n';
import {
  trackTrayClearAll,
  trackTrayCompareNow,
  trackTrayRemove,
  type TrayPlan,
} from './tracking';

/** Comparing needs two plans; one selected keeps the tray open with the CTA disabled. */
const MIN_PLANS_TO_COMPARE = 2;

/**
 * The tray's buttons are 32px tall with the regular side padding, a step up from the small button.
 * From md the small button grows to 36px and 16px text; the tray keeps 32px and 14px there.
 */
const BUTTON_SIZE =
  'min-h-8 px-spacing/padding/x md:h-8 md:text-font-size/sm md:leading-line-height/sm';

/** From md each row lines up with the page's content column: same side padding, same max width. */
const CONTENT_COLUMN =
  'md:px-spacing/padding/section-x lg:mx-auto lg:box-content lg:max-w-[--breakpoints-content-narrow-3-lg]';

/**
 * Docks to the bottom of the viewport from the first selection. Row 1: one logo tile per selected
 * plan (× removes it) followed by empty slots up to the cap. Row 2: Clear and the Compare CTA. While
 * shown it pads the document bottom by its own height so no card's compare row hides behind it.
 */
export const CompareTray = () => {
  const compare = useCompare();
  const context = useCmsClientContext();
  // A state, not a ref: the tray renders nothing until the first selection, and both effects have
  // to see it appear.
  const [tray, setTray] = useState<HTMLDivElement | null>(null);
  const shown = !!compare && compare.selected.length > 0;
  const announcement = useSelectionAnnouncement();
  // What the tray covers, for the Sort & Filter button and rail to stay clear of.
  usePinnedBottomHeight(tray, shown);

  useEffect(() => {
    if (!shown || !tray) return;
    const body = document.body;
    const previous = body.style.paddingBottom;
    const apply = () => {
      body.style.paddingBottom = `${tray.offsetHeight}px`;
    };
    apply();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(apply);
    observer?.observe(tray);
    return () => {
      observer?.disconnect();
      body.style.paddingBottom = previous;
    };
  }, [shown, tray]);

  // The announcement outlives the tray: removing the last item still says so.
  const live = (
    <p
      aria-live="polite"
      className="sr-only"
      data-component="compare-announcer"
    >
      {announcement}
    </p>
  );
  const variant = compare ? compareVariantOf(compare.vertical) : undefined;
  if (!compare || !shown || !variant) return live;

  const staticText = getClientTranslations(TRANSLATIONS, context);
  const { selected, cap, productsById } = compare;
  const layout = COMPARE_VARIANT_LAYOUT[variant];
  const copy = compareVariantCopy(variant, staticText);
  const canCompare = selected.length >= MIN_PLANS_TO_COMPARE;
  const href = compare.compareHref(selected);
  const freePlaces = Math.max(0, cap - selected.length);
  const emptySlots =
    layout.trayEmptySlots === 'all' ? freePlaces : Math.min(1, freePlaces);
  const counted = layout.trayCta === 'counted';

  // The tray's plans for the tracking plan: every selected plan, in tray order; one no card has
  // registered goes by its id alone.
  const trayPlan = (id: string, index: number): TrayPlan => {
    const product = productsById.get(id);
    return product ? { product, index } : { id, index };
  };
  const trayPlans = (): TrayPlan[] => selected.map(trayPlan);
  const tracking = compare.tracking;
  const clearLabel = tr('compare_clear', staticText);
  const ctaLabel = copy.trayCta(selected.length);
  const clear = () => {
    if (tracking)
      trackTrayClearAll(tracking, {
        clickText: clearLabel,
        plans: trayPlans(),
      });
    compare.clear();
  };
  const remove = (id: string, index: number) => {
    if (tracking)
      trackTrayRemove(tracking, {
        plan: trayPlan(id, index),
      });
    compare.remove(id);
  };

  return (
    <>
      {live}
      <div
        ref={setTray}
        role="region"
        aria-label={tr('compare_tray_label', staticText)}
        data-component="compare-tray"
        data-count={selected.length}
        className="fixed bottom-0 left-0 z-[60] w-full bg-fill/card shadow-[4px_4px_24px_-3px_rgba(0,0,0,0.2)]"
      >
        <div className="border-b border-borders/default">
          <ul
            className={classNames(
              'flex gap-spacing/padding/x overflow-x-auto px-spacing/padding/x pb-spacing/padding/y-sm pt-spacing/padding/y',
              CONTENT_COLUMN,
            )}
            aria-label={copy.pageTitle}
          >
            {selected.map((id, index) => {
              const product = productsById.get(id);
              const name = product?.product_name ?? id;
              const picture = compareImageOf(product, layout.columnImage);
              const image = picture?.src;
              return (
                <li
                  key={id}
                  data-product-id={id}
                  className="relative flex h-12 w-[75px] shrink-0 items-center justify-center rounded-radius/tooltip border border-borders/default bg-fill/card p-spacing/gap/x-sm md:h-18 md:w-28"
                >
                  {image ? (
                    <CmsImage
                      src={image}
                      alt={
                        picture?.isProductImage
                          ? name
                          : (product?.provider?.provider_name ?? name)
                      }
                      width={59}
                      height={32}
                      sizes={{ _: 59, sm: 80 }}
                      className="size-full object-contain"
                    />
                  ) : (
                    <span className="mhg-alpha-typography-caption truncate">
                      {name}
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={tr('compare_remove_plan', staticText, {
                      'plan-name': name,
                    })}
                    onClick={() => remove(id, index)}
                    // Overhangs the small mobile tile; sits 6px inside the wider desktop tile, as each design draws
                    // it. Offsets count from inside the tile's 1px border.
                    className="absolute right-[-7px] top-[-7px] flex size-5 items-center justify-center md:right-[5px]"
                  >
                    {/* A filled "cancel" glyph: the icon font only ships the outlined style. */}
                    <span className="flex size-4 items-center justify-center rounded-full bg-colors/neutral-gray-600 text-colors/neutral-white">
                      <MaterialIcon icon="close" px={12} />
                    </span>
                  </button>
                </li>
              );
            })}
            {Array.from({ length: emptySlots }, (_, index) => (
              <li
                key={`empty-${index}`}
                aria-hidden="true"
                data-component="compare-tray-slot"
                className="flex h-12 w-[75px] shrink-0 items-center justify-center rounded-radius/tooltip border border-dashed border-borders/default bg-fill/card text-text/secondary-lighter md:h-18 md:w-28"
              >
                <MaterialIcon icon="add_circle" px={22} />
              </li>
            ))}
          </ul>
        </div>
        <div
          className={classNames(
            'flex items-center justify-between gap-spacing/gap/x px-spacing/margin/x py-spacing/padding/y-sm',
            CONTENT_COLUMN,
          )}
        >
          <Button
            variant="outline"
            size="small"
            onClick={clear}
            // The counted CTA is wider; on a phone Clear condenses to text so the CTA keeps its room.
            className={classNames(
              BUTTON_SIZE,
              counted && 'hidden md:inline-flex',
            )}
          >
            {clearLabel}
          </Button>
          {counted && (
            <Button
              variant="text"
              size="small"
              onClick={clear}
              className={classNames(BUTTON_SIZE, 'md:hidden')}
            >
              {clearLabel}
            </Button>
          )}
          {canCompare ? (
            <Button
              as="a"
              size="small"
              // A plain anchor on purpose: `FeLink` is the outbound-link workaround and opens any
              // href containing "ad" in a new tab, which a destination such as Canada triggers.
              href={href}
              data-testid="compare-cta"
              styleType={counted ? 'cta' : 'primary'}
              className={BUTTON_SIZE}
              onClick={() => {
                if (tracking)
                  trackTrayCompareNow(tracking, {
                    clickText: ctaLabel,
                    plans: trayPlans(),
                  });
              }}
            >
              {ctaLabel}
            </Button>
          ) : (
            <span
              // A link that cannot be followed yet: the role makes `aria-disabled` mean something,
              // and it stays reachable so the keyboard reader hears why it is disabled.
              role="link"
              aria-disabled="true"
              tabIndex={0}
              data-testid="compare-cta"
              className={classNames(
                'mhg-alpha-btn mhg-alpha-btn-small cursor-default bg-colors/neutral-gray-300 text-colors/neutral-gray-600',
                BUTTON_SIZE,
              )}
            >
              {copy.trayCtaDisabled(MIN_PLANS_TO_COMPARE - selected.length)}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * A polite announcement of what changed in the selection, so adding or removing is never silent
 * for a screen reader. Diffs the selection against the last one it saw, starting once the provider
 * has restored it (a restored selection is not news). Several items at once are announced as a
 * count.
 */
function useSelectionAnnouncement(): string {
  const compare = useCompare();
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const previous = useRef<string[] | null>(null);
  const [message, setMessage] = useState('');
  const selected = compare?.selected;
  const ready = compare?.ready ?? false;

  useEffect(() => {
    if (!selected || !ready) return;
    const before = previous.current;
    previous.current = [...selected];
    if (!before) return;
    const nameOf = (id: string) =>
      compare?.productsById.get(id)?.product_name ?? id;
    const added = selected.filter((id) => !before.includes(id));
    const removed = before.filter((id) => !selected.includes(id));
    if (added.length > 1)
      setMessage(
        tr('compare_announce_added_count', staticText, {
          count: String(added.length),
        }),
      );
    else if (added.length === 1)
      setMessage(
        tr('compare_announce_added', staticText, { name: nameOf(added[0]) }),
      );
    else if (removed.length > 1)
      setMessage(
        tr('compare_announce_removed_count', staticText, {
          count: String(removed.length),
        }),
      );
    else if (removed.length === 1)
      setMessage(
        tr('compare_announce_removed', staticText, {
          name: nameOf(removed[0]),
        }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the selection only
  }, [selected, ready]);

  return message;
}
